import type { Language } from './languages'

/** One language's messages, keyed by message id. */
export type Messages = Readonly<Record<string, string>>

/**
 * Every message file: `messages/{language}.json` in a feature's folder or a shared one, found at
 * build time so a new feature registers nothing. Each file is its own chunk, fetched only when its
 * language is chosen. `src/test/messages.ts` keeps an eager copy of these patterns.
 */
const messageFiles = import.meta.glob<Messages>(
  ['/src/features/**/messages/*.json', '/src/shared/**/messages/*.json'],
  { import: 'default' },
)

/** The paths found, so a test can check the eager copy finds the same files. */
export const messageFilePaths: readonly string[] = Object.keys(messageFiles)

/** The language a message file is for, from its name. */
export function languageOfFile(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1, -'.json'.length)
}

const loading = new Map<Language, Promise<Messages>>()

/** Loads and merges every message file for one language, once. */
export function loadMessages(language: Language): Promise<Messages> {
  const pending = loading.get(language)
  if (pending !== undefined) {
    return pending
  }

  const files = Object.entries(messageFiles)
    .filter(([path]) => languageOfFile(path) === language)
    .map(async ([, load]) => await load())

  const messages = Promise.all(files).then((parts) =>
    Object.freeze(Object.fromEntries(parts.flatMap((part) => Object.entries(part)))),
  )

  // Forget a failure, so asking again retries rather than replaying it.
  messages.catch(() => {
    loading.delete(language)
  })

  loading.set(language, messages)
  return messages
}
